import { Children, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { useLoadingCallback } from '../lib/useLoadingCallback';
import { cn } from '../lib/utils';
import { Button } from './Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  type DialogContentProps,
  type DialogSize,
  type DialogVariant,
} from './Dialog';
import { Input } from './Input';

interface ConfirmationDialogProps {
  /** Figma's four Dialog widths. Defaults to Dialog's `s` (480px). */
  size?: DialogSize;
  /** Light (default) or dark surface. Passed through to DialogContent. */
  variant?: DialogVariant;
  /** Whether the dialog is currently open */
  open: boolean;
  /** Callback to control dialog open state */
  onOpenChange: (open: boolean) => void;
  /** Dialog title */
  title: string;
  /** Dialog description/message, rendered in the header under the title. Omit when the
   * message belongs in the body instead — pass it as `children` there. */
  description?: ReactNode;
  /** Text for the cancel button */
  cancelText?: string;
  /** Text for the confirm button */
  confirmText?: string;
  /** Variant for the confirm button */
  confirmVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  /** Callback when cancel button is clicked */
  onCancel?: () => void;
  /** Callback when confirm button is clicked */
  onConfirm?: (e: MouseEvent<HTMLButtonElement>, inputValue?: string) => Promise<unknown> | void;
  /** Additional CSS classes for the dialog content */
  className?: string;
  /** Test ID for E2E testing */
  'data-testid'?: string;
  /** Test id for the cancel button. */
  cancelTestId?: string;
  /** Test id for the confirm button. */
  confirmTestId?: string;
  /** Whether to show an input field */
  showInput?: boolean;
  /** Value for the input field */
  inputValue?: string;
  /** Extra body content, rendered below the description (and above the input, if any). */
  children?: ReactNode;
  /**
   * Where the dialog portals to. Defaults to the `PortalContainerProvider` target,
   * and then to `document.body`. Pass the fullscreen element so the dialog stays
   * visible while the player is fullscreen.
   */
  container?: DialogContentProps['container'];
  /**
   * When false, a click on the overlay does not close the dialog.
   * Escape and the cancel button still close it.
   */
  closeOnOutsideClick?: boolean;
}

export function ConfirmationDialog({
  size,
  variant,
  open,
  onOpenChange,
  title,
  description,
  cancelText = 'Cancel',
  confirmText = 'Confirm',
  confirmVariant = 'default',
  onCancel,
  onConfirm,
  className,
  'data-testid': dataTestId,
  cancelTestId,
  confirmTestId,
  showInput,
  inputValue,
  children,
  container,
  closeOnOutsideClick = true,
}: ConfirmationDialogProps) {
  const [inputValueState, setInputValue] = useState(inputValue ?? '');
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const [isLoading, handleClick] = useLoadingCallback(undefined, async (e) => {
    const result = await onConfirm?.(e, inputValueState);
    // A false result means the caller reported a failure and the dialog stays open.
    if (result !== false) {
      onOpenChange(false);
    }
  });

  // `Children.count` counts a null/undefined/false child as 1; `toArray` drops them.
  const hasBody = Children.toArray(children).length > 0 || showInput;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Escape and overlay dismiss still fire while confirm is in flight.
        if (!next && isLoading) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        size={size}
        variant={variant}
        className={className}
        showCloseButton={false}
        data-testid={dataTestId}
        container={container}
        onOpenAutoFocus={(event) => {
          if (showInput) return;
          event.preventDefault();
          confirmButtonRef.current?.focus();
        }}
        disableEscapeKeyDown={isLoading}
        onInteractOutside={(event) => {
          if (isLoading || !closeOnOutsideClick) event.preventDefault();
        }}
      >
        <form
          className={cn('flex flex-col', hasBody && 'gap-4')}
          onSubmit={(event) => event.preventDefault()}
        >
          <DialogHeader hasCloseButton={false} className={hasBody ? undefined : 'mb-0 border-b-0'}>
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          {children}
          {/* Input Field */}
          {showInput && (
            <Input
              value={inputValueState}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
          )}
          <DialogFooter className={hasBody ? undefined : 'mt-0 border-t-0'}>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
              data-testid={cancelTestId}
            >
              {cancelText}
            </Button>
            <Button
              ref={confirmButtonRef}
              type="submit"
              variant={confirmVariant}
              onClickAsync={handleClick}
              disabled={isLoading}
              data-testid={confirmTestId}
            >
              {confirmText}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type ConfirmOptions = Omit<ConfirmationDialogProps, 'open' | 'onOpenChange'>;
export const confirm = (options: ConfirmOptions): void => {
  const container = document.createElement('div');
  document.body.appendChild(container);
  const root = createRoot(container);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      root.unmount();
      container.remove();
    }
  };

  root.render(<ConfirmationDialog {...options} open={true} onOpenChange={handleOpenChange} />);
};
